import { Box, Typography } from '@mui/material';
import { ResponsiveLineCanvas } from '@nivo/line';
import { LTTB } from 'downsample';
import React, { useMemo } from 'react';

export type DVHLineChartProps = {
    data: CurveData[];
    normalizationValueGy: number;
    resolution: number;
};

export type CurveData = {
    data: { x: number; y: number }[];
    color: string;
    name: string;
    visible: boolean;
    maxDose: number;
    minDose: number;
    meanDose: number;
    roiNumber: number;
};

const DVHLineChart: React.FC<DVHLineChartProps> = ({
    data,
    normalizationValueGy,
    resolution,
}) => {
    const getMax = (curves: Array<CurveData>) => {
        let max = -Infinity;

        for (const curve of curves) {
            for (const val of curve.data) {
                max = val.x > max ? val.x : max;
            }
        }
        return max;
    };

    const maxXValue = useMemo(
        () => Math.max(getMax(data), normalizationValueGy),
        [data, normalizationValueGy]
    );

    const chartData = useMemo(
        () =>
            data.map((curve) => ({
                id: curve.name,
                data: LTTB(curve.data, resolution ?? 100) as {
                    x: number;
                    y: number;
                }[],
                color: curve.color,
            })),
        [data, resolution]
    );

    const cursorPointToRelativeDose = (point: string | number): string => {
        if (point !== undefined) {
            const pointValue =
                typeof point === 'string' ? parseFloat(point) : point;
            return `${((100 * pointValue) / normalizationValueGy).toFixed(1)}`;
        }
        return '';
    };

    const idToDoseStats = (id: string): string => {
        const dvh = data.find(
            (curve) => curve.name === chartIdToStructureId(id)
        );
        if (dvh) {
            return `Max: ${dvh.maxDose.toFixed(
                1
            )} Gy, Min: ${dvh.minDose.toFixed(
                1
            )} Gy, Mean: ${dvh.meanDose.toFixed(1)} Gy`;
        }
        return '';
    };

    const chartIdToStructureId = (id: string): string => {
        const delimiterIndex = id.lastIndexOf('.');
        if (delimiterIndex !== -1) {
            return id.slice(0, delimiterIndex);
        }
        return id;
    };

    return (
        <ResponsiveLineCanvas
            data={chartData}
            margin={{ top: 60, right: 30, bottom: 50, left: 60 }}
            xScale={{ type: 'linear', min: 0, max: maxXValue }}
            yScale={{ type: 'linear', min: 0, max: 100 }}
            data-testid="dvh-line-chart"
            axisRight={null}
            enableGridX={true}
            enableGridY={true}
            lineWidth={1.5}
            enablePoints={false}
            curve="linear"
            enableArea={chartData.length <= 2 && true}
            areaOpacity={0.5}
            theme={{
                axis: {
                    ticks: {
                        line: {
                            stroke: '#555555',
                        },
                        text: {
                            fill: '#FFFFFF',
                        },
                    },
                    legend: {
                        text: {
                            fill: '#aaaaaa',
                        },
                    },
                },
                crosshair: {
                    line: {
                        stroke: '#ffffff',
                    },
                },
            }}
            axisBottom={{
                legend: 'Dose (Gy)',
                legendOffset: 35,
                legendPosition: 'middle',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
            }}
            axisTop={{
                legend: 'Relative Dose (%)',
                legendOffset: -40,
                legendPosition: 'middle',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: (value) =>
                    `${((value / normalizationValueGy) * 100).toFixed(1)}%`,
            }}
            axisLeft={{
                legend: 'Ratio of Total Structure Volume (%)',
                legendOffset: -40,
                legendPosition: 'middle',
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
            }}
            colors={(chartDataObj) => chartDataObj.color}
            tooltip={({ point }) => {
                const structureName = chartIdToStructureId(point.id);
                const doseValue =
                    typeof point.data.x === 'number'
                        ? `${point.data.x.toFixed(1)} Gy`
                        : `${point.data.xFormatted} Gy`;
                const volumeValue =
                    typeof point.data.y === 'number'
                        ? `${point.data.y.toFixed(1)} %`
                        : `${point.data.yFormatted} %`;
                const backgroundColor = point.serieColor;
                const relativeDoseInPercent = cursorPointToRelativeDose(
                    point.data.xFormatted
                );
                const tooltipLeaningDir =
                    parseFloat(relativeDoseInPercent) < 50 ? 'left' : 'right';
                return (
                    <Box
                        sx={{
                            background: 'black',
                            padding: '10px',
                            borderRadius: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            fontSize: '8px',
                            position: 'absolute',
                            minWidth: '360px',
                            [tooltipLeaningDir]: '1em',
                        }}
                        data-testid="dvh-tooltip"
                    >
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    marginRight: '5px',
                                    backgroundColor,
                                }}
                            />
                            <Typography sx={{ fontWeight: 'bold' }}>
                                {structureName}
                            </Typography>
                        </Box>
                        <Typography>
                            {`Dose: ${doseValue} (${relativeDoseInPercent} %)`}
                        </Typography>
                        <Typography>{`Volume: ${volumeValue}`}</Typography>
                        <Typography>{idToDoseStats(point.id)}</Typography>
                    </Box>
                );
            }}
        />
    );
};

export default DVHLineChart;
